import { useState, useEffect, useCallback, useRef } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, DISPLAY, BODY, MONO } from '../lib/constants';
import { Horizon, Mo, Bd, Dp, Label, Ic, ProgressArc } from './shared';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function useInterestTicker(perSecond) {
  const [accumulated, setAccumulated] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (!perSecond) return;
    startRef.current = Date.now();
    setAccumulated(0);
    const iv = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      setAccumulated(elapsed * perSecond);
    }, 100);
    return () => clearInterval(iv);
  }, [perSecond]);

  return accumulated;
}

// Compact widget for Home screen
export function CountdownWidget({ onExpand }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/countdown`, { credentials: 'include' }).then(r => r.json()).then(setData).catch(() => {});
  }, []);

  const liveInterest = useInterestTicker(data?.total?.per_second_interest);

  if (!data?.target) return null;

  return (
    <div onClick={onExpand} data-testid="countdown-widget" style={{
      padding: 16, background: 'var(--surface)', border: `1px solid ${FAINT}`,
      cursor: 'pointer', position: 'relative', overflow: 'hidden'
    }}>
      {/* Subtle glow */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 60, background: `radial-gradient(ellipse at 50% 0%, rgba(232,168,56,0.06) 0%, transparent 70%)`, pointerEvents: 'none' }} />

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 16 }}>
        {/* Countdown number */}
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
          <Dp style={{ fontSize: 38, fontWeight: 800, color: 'var(--gold)', display: 'block', letterSpacing: -2, lineHeight: 1 }}>{data.target.days_left}</Dp>
          <Mo style={{ fontSize: 7, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2 }}>DAYS LEFT</Mo>
        </div>

        {/* Details */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Mo style={{ fontSize: 10, fontWeight: 700, color: 'var(--ember)', letterSpacing: 1.5 }}>TARGET</Mo>
          </div>
          <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)', display: 'block' }}>{data.target.name}</Bd>
          <Mo style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', display: 'block', marginTop: 2 }}>${data.target.balance.toLocaleString()}</Mo>

          {/* Live interest ticker */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
            <div className="emb-p" style={{ width: 5, height: 5, borderRadius: '50%', background: EMBER }} />
            <Mo style={{ fontSize: 9, color: 'var(--ember)', letterSpacing: 0.3 }}>
              ${liveInterest.toFixed(2)} interest since you opened the app
            </Mo>
          </div>
        </div>
      </div>
    </div>
  );
}

// Full countdown screen
export function CountdownScreen() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/countdown`, { credentials: 'include' }).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const liveInterest = useInterestTicker(data?.total?.per_second_interest);

  if (loading) return (
    <div data-testid="countdown-loading" style={{ padding: '60px 28px', textAlign: 'center', position: 'relative' }}>
      <Horizon intensity={0.4} />
      <div className="pulse-a" style={{ position: 'relative', zIndex: 1 }}>{Ic.flame(32, GOLD)}</div>
    </div>
  );

  if (!data?.target) return (
    <div style={{ padding: '40px 28px', textAlign: 'center' }}>
      <Bd style={{ fontSize: 14, color: 'var(--mute)' }}>No active debts found</Bd>
    </div>
  );

  const t = data.target;
  const a = data.total;
  const daysD = Math.floor(t.days_left);
  const hours = Math.floor((t.days_left % 1) * 24);

  return (
    <div data-testid="countdown-screen" className="dettio-screen">
      <Horizon intensity={0.7} />
      <div className="dettio-screen__inner">
        {/* Hero countdown */}
        <div className="dettio-screen__center" style={{ marginBottom: 28 }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--ember)', letterSpacing: 2, display: 'block', marginBottom: 8 }}>KILLING {t.name.toUpperCase()}</Mo>
          <Dp style={{ fontSize: 72, fontWeight: 800, color: 'var(--gold)', display: 'block', letterSpacing: -4, lineHeight: 1 }}>{daysD}</Dp>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2, marginTop: 4 }}>DAYS UNTIL DEAD</Mo>
          <Bd style={{ fontSize: 13, color: 'var(--mute)', marginTop: 8 }}>{t.kill_date}</Bd>
        </div>

        {/* Target debt card */}
        <div className="dettio-card" style={{ borderLeft: `3px solid ${GOLD}` }}>
          <div className="dettio-flex dettio-flex--between dettio-flex--center" style={{ marginBottom: 10 }}>
            <div>
              <Bd style={{ fontSize: 16, fontWeight: 600, color: 'var(--light)' }}>{t.name}</Bd>
              <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>{t.apr}% APR · ${t.monthly_payment}/mo</Mo>
            </div>
            <Mo style={{ fontSize: 18, fontWeight: 700, color: 'var(--gold)' }}>${t.balance.toLocaleString()}</Mo>
          </div>
          <div style={{ height: 4, background: 'var(--faint)', marginBottom: 6 }}>
            <div style={{ height: '100%', width: `${t.pct_paid}%`, background: `linear-gradient(90deg,${EMBER},${GOLD})` }} />
          </div>
          <Mo style={{ fontSize: 9, color: 'var(--gold)' }}>{t.pct_paid}% conquered</Mo>
        </div>

        {/* Live interest ticker */}
        <div data-testid="interest-ticker" className="dettio-card" style={{
          background: `${EMBER}08`, border: `1px solid ${EMBER}20`, textAlign: 'center'
        }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--ember)', letterSpacing: 2, display: 'block', marginBottom: 8 }}>INTEREST TICKING RIGHT NOW</Mo>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 2 }}>
            <Mo style={{ fontSize: 32, fontWeight: 700, color: 'var(--ember)', fontVariantNumeric: 'tabular-nums' }}>
              ${liveInterest.toFixed(2)}
            </Mo>
          </div>
          <Bd style={{ fontSize: 11, color: 'var(--mute)', marginTop: 6 }}>Since you opened this screen</Bd>
          <div style={{ marginTop: 10, display: 'flex', gap: 6, justifyContent: 'center' }}>
            <div style={{ padding: '6px 10px', background: 'var(--faint)' }}>
              <Mo style={{ fontSize: 7, color: 'var(--mute)', display: 'block', marginBottom: 2 }}>PER SECOND</Mo>
              <Mo style={{ fontSize: 11, fontWeight: 700, color: 'var(--ember)' }}>${a.per_second_interest.toFixed(6)}</Mo>
            </div>
            <div style={{ padding: '6px 10px', background: 'var(--faint)' }}>
              <Mo style={{ fontSize: 7, color: 'var(--mute)', display: 'block', marginBottom: 2 }}>PER DAY</Mo>
              <Mo style={{ fontSize: 11, fontWeight: 700, color: 'var(--ember)' }}>${a.daily_interest.toFixed(2)}</Mo>
            </div>
            <div style={{ padding: '6px 10px', background: 'var(--faint)' }}>
              <Mo style={{ fontSize: 7, color: 'var(--mute)', display: 'block', marginBottom: 2 }}>PER MONTH</Mo>
              <Mo style={{ fontSize: 11, fontWeight: 700, color: 'var(--ember)' }}>${(a.daily_interest * 30.44).toFixed(2)}</Mo>
            </div>
          </div>
        </div>

        {/* Total freedom countdown */}
        <Label>TOTAL FREEDOM</Label>
        <div className="dettio-card">
          <div className="dettio-flex" style={{ justifyContent: 'center', marginBottom: 12 }}>
            <ProgressArc value={a.pct_free} size={120} />
          </div>
          <div className="dettio-flex dettio-flex--gap-sm">
            {[
              { l: 'ALL FREE IN', v: `${a.all_free_days}d`, c: GOLD },
              { l: 'FREE BY', v: a.all_free_date, c: GOLD },
              { l: 'DEBTS LEFT', v: a.debts_count, c: LIGHT },
            ].map(s => (
              <div key={s.l} className="dettio-stat-box">
                <Mo style={{ fontSize: 7, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 3 }}>{s.l}</Mo>
                <Mo style={{ fontSize: 13, fontWeight: 700, color: s.c }}>{s.v}</Mo>
              </div>
            ))}
          </div>
        </div>

        {/* Add to home screen prompt */}
        <div className="dettio-card" style={{ background: `${GOLD}08`, border: `1px solid ${GOLD}15` }}>
          <div className="dettio-flex dettio-flex--center dettio-flex--gap-sm" style={{ marginBottom: 6 }}>
            {Ic.download(14, GOLD)}
            <Mo style={{ fontSize: 9, fontWeight: 700, color: 'var(--gold)', letterSpacing: 1 }}>ADD TO HOME SCREEN</Mo>
          </div>
          <Bd style={{ fontSize: 11, color: 'var(--mute)', lineHeight: 1.6 }}>
            Tap your browser's share button → "Add to Home Screen" to see this countdown every time you unlock your phone.
          </Bd>
        </div>

        {/* Motivation */}
        <div style={{ padding: 14, background: 'var(--faint)' }}>
          <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--mute)', letterSpacing: 0.5, lineHeight: 1.7 }}>
            Every second you look at this, ${a.per_second_interest.toFixed(4)} ticks away in interest. Every extra dollar you throw at {t.name} stops that clock faster. {t.days_left} days isn't forever — it's a countdown, and you're winning.
          </Mo>
        </div>
      </div>
    </div>
  );
}

// Standalone countdown page (for home screen shortcut)
export function CountdownStandalone() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/countdown`, { credentials: 'include' }).then(r => r.json()).then(setData).catch(() => {});
    const iv = setInterval(() => {
      fetch(`${API}/countdown`, { credentials: 'include' }).then(r => r.json()).then(setData).catch(() => {});
    }, 60000);
    return () => clearInterval(iv);
  }, []);

  const liveInterest = useInterestTicker(data?.total?.per_second_interest);

  if (!data?.target) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--base)' }}>
      <div className="pulse-a">{Ic.flame(32, GOLD)}</div>
    </div>
  );

  const t = data.target;
  const a = data.total;

  return (
    <div data-testid="countdown-standalone" style={{
      maxWidth: 430, margin: '0 auto', background: 'var(--base)', minHeight: '100vh',
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      padding: '40px 28px', position: 'relative', textAlign: 'center'
    }}>
      <Horizon intensity={0.8} />
      <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 32 }}>
          <span className="str-b">{Ic.flame(14, GOLD)}</span>
          <Mo style={{ fontSize: 9, fontWeight: 700, color: 'var(--mute)', letterSpacing: 2 }}>DETTIO</Mo>
        </div>

        {/* Big countdown */}
        <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--ember)', letterSpacing: 2, display: 'block', marginBottom: 6 }}>KILLING {t.name.toUpperCase()}</Mo>
        <Dp style={{ fontSize: 96, fontWeight: 800, color: 'var(--gold)', display: 'block', letterSpacing: -6, lineHeight: 1 }}>{t.days_left}</Dp>
        <Mo style={{ fontSize: 12, fontWeight: 600, color: 'var(--mute)', letterSpacing: 3, marginTop: 8, display: 'block' }}>DAYS</Mo>

        {/* Balance */}
        <div style={{ marginTop: 28, marginBottom: 8 }}>
          <Mo style={{ fontSize: 28, fontWeight: 700, color: 'var(--light)' }}>${t.balance.toLocaleString()}</Mo>
        </div>
        <Mo style={{ fontSize: 9, color: 'var(--gold)' }}>{t.pct_paid}% conquered · {t.kill_date}</Mo>

        {/* Live interest */}
        <div style={{ marginTop: 28, padding: 16, background: `${EMBER}06`, border: `1px solid ${EMBER}15` }}>
          <Mo style={{ fontSize: 10, color: 'var(--ember)', letterSpacing: 1.5, display: 'block', marginBottom: 6 }}>INTEREST RIGHT NOW</Mo>
          <Mo style={{ fontSize: 24, fontWeight: 700, color: 'var(--ember)', fontVariantNumeric: 'tabular-nums' }}>
            ${liveInterest.toFixed(2)}
          </Mo>
          <Bd style={{ fontSize: 10, color: 'var(--mute)', marginTop: 4 }}>${a.daily_interest.toFixed(2)}/day across all debts</Bd>
        </div>

        {/* Total progress */}
        <div style={{ marginTop: 20, display: 'flex', gap: 6 }}>
          <div style={{ flex: 1, padding: '12px 8px', background: 'var(--faint)', textAlign: 'center' }}>
            <Mo style={{ fontSize: 7, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 3 }}>TOTAL FREE</Mo>
            <Mo style={{ fontSize: 18, fontWeight: 700, color: 'var(--gold)' }}>{a.pct_free}%</Mo>
          </div>
          <div style={{ flex: 1, padding: '12px 8px', background: 'var(--faint)', textAlign: 'center' }}>
            <Mo style={{ fontSize: 7, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 3 }}>ALL FREE</Mo>
            <Mo style={{ fontSize: 18, fontWeight: 700, color: 'var(--gold)' }}>{a.all_free_date}</Mo>
          </div>
        </div>
      </div>
    </div>
  );
}
